import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useNavigation } from '@react-navigation/native';

import { GASbkEvents } from '@/feature/analytics/sbk/ga-sbk-events';
import { SbkLobbyParams, SweepsLobbyTabNavigationProp } from '@/navigation/sweeps/types';

import { LaunchpadItem, buildLaunchpadItems } from '../utils/navigation-launchpad-utils';
import { useLiveHubEnabled } from './use-live-hub-enabled';
import { useNavigationEntries } from './use-navigation-entries';
import { useSbkLobbySectionSelection } from './use-sbk-lobby-section-selection';

export const useNavigationLaunchpad = () => {
    const { t } = useTranslation('common');
    const navigation = useNavigation<SweepsLobbyTabNavigationProp>();
    const liveHubEnabled = useLiveHubEnabled();
    const { data: entries, isLoading } = useNavigationEntries();
    const selectedId = useSbkLobbySectionSelection(state => state.selectedTile);
    const setSelectedTile = useSbkLobbySectionSelection(state => state.actions.setSelectedTile);

    const items = useMemo(
        () =>
            buildLaunchpadItems(entries ?? [], liveHubEnabled, {
                explore: t('explore'),
                promos: t('promos'),
                live: t('live'),
            }),
        [entries, liveHubEnabled, t]
    );

    const select = useCallback(
        (params: SbkLobbyParams, tile: string) => {
            navigation.replaceParams(params);
            setSelectedTile(tile);
        },
        [navigation, setSelectedTile]
    );

    const onSelect = useCallback(
        (item: LaunchpadItem) => {
            switch (item.kind) {
                case 'promos':
                    navigation.navigate('LobbyPromohub');
                    return;
                case 'explore':
                case 'live':
                    select({ leagueId: item.id }, item.id);
                    return;
                case 'league':
                    GASbkEvents.clickLeague(item.entryId, item.label);
                    select({ leagueId: item.entryId }, item.id);
                    return;
                case 'sport':
                    select({ sportId: item.entryId }, item.id);
                    return;
                case 'competition':
                    select({ competitionId: item.entryId }, item.id);
                    return;
            }
        },
        [navigation, select]
    );

    const onViewAllPress = useCallback(() => navigation.navigate('SbkAllLeaguesScreen'), [navigation]);

    return { items, isLoading, selectedId, onSelect, onViewAllPress };
};
